import React, { useState } from "react";
import { agrogestao } from "@/api/agrogestaoClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Loader2, ScanLine, CheckCircle2, AlertCircle, Sparkles } from "lucide-react";
import { extrairDadosCodigoBarras } from "@/utils/boletoUtils";

export default function LeitorCodigoBarras({ onDadosExtraidos }) {
  const [codigo, setCodigo] = useState("");
  const [loading, setLoading] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [erro, setErro] = useState(null);

  const handleExtrair = async () => {
    if (!codigo.trim()) return;
    
    setLoading(true);
    setErro(null);
    setResultado(null);

    const inicio = Date.now();
    try {
      const dadosLocais = extrairDadosCodigoBarras(codigo);
      const processamento = Date.now() - inicio;
      if (dadosLocais && (dadosLocais.valor || dadosLocais.data_vencimento || dadosLocais.codigo_barras)) {
        const payload = { ...dadosLocais, processamento_ms: processamento, origem: "local" };
        setResultado(payload);
        onDadosExtraidos(payload);
        if (payload.inconsistencias?.length) {
          setErro(payload.inconsistencias.join(" • "));
        }
        try {
          const propriedadeId = localStorage.getItem("propriedade_selecionada");
          await agrogestao.access.logAudit({
            propriedade_id: propriedadeId,
            acao: "leitor_codigo_barras",
            table: "contas_pagar",
            status: payload.valido ? "success" : "warning",
            details: {
              tipo: payload.tipo,
              processamento_ms: processamento,
              inconsistencias: payload.inconsistencias
            }
          });
        } catch {}
        if (payload.valido || payload.codigo_barras) {
          setLoading(false);
          return;
        }
      }
      const response = await agrogestao.integrations.Core.InvokeLLM({
        prompt: `Analise este código de barras de boleto brasileiro e extraia as informações:
        
Código: ${codigo}

Extraia:
- valor (em reais, número decimal)
- data_vencimento (formato YYYY-MM-DD)
- fornecedor (nome do banco ou empresa, se identificável)
- categoria (uma de: folha_pagamento, impostos, energia, agua, aluguel, financiamento, insumos, combustivel, manutencao, veterinario, transporte, seguro, consultoria, outros)

Se não conseguir identificar algum campo, retorne null para ele.`,
        response_json_schema: {
          type: "object",
          properties: {
            valor: { type: ["number", "null"] },
            data_vencimento: { type: ["string", "null"] },
            fornecedor: { type: ["string", "null"] },
            categoria: { type: ["string", "null"] }
          }
        }
      });
      if (response && (response.valor || response.data_vencimento || response.fornecedor)) {
        const payload = {
          ...response,
          valido: false,
          codigo_input: codigo,
          codigo_normalizado: String(codigo || "").replace(/\D/g, ""),
          inconsistencias: ["Validação automática indisponível para este código"],
          log: [{ etapa: "ia", detalhe: "Leitura assistida por IA" }],
          processamento_ms: Date.now() - inicio,
          origem: "llm"
        };
        setResultado(payload);
        onDadosExtraidos(payload);
      } else {
        setErro("Não foi possível extrair dados deste código. Verifique se está correto.");
      }
    } catch (e) {
      setErro("Erro ao processar código de barras.");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
  };

  return (
    <Card className="bg-gradient-to-br from-indigo-50 to-purple-50 border-indigo-200">
      <CardContent className="p-4 space-y-4">
        <div className="flex items-center gap-2 text-indigo-700">
          <ScanLine className="w-5 h-5" />
          <span className="font-semibold">Leitor de Código de Barras</span>
          <Sparkles className="w-4 h-4 text-amber-500" />
        </div>

        <div className="space-y-2">
          <Label className="text-sm text-indigo-600">Cole a linha digitável ou código de barras</Label>
          <div className="flex gap-2">
            <Input
              placeholder="Ex: 23793.38128 60000.000003 00000.000400 1 84340000012345"
              value={codigo}
              onChange={(e) => setCodigo(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !loading && codigo.trim()) {
                  e.preventDefault();
                  handleExtrair();
                }
              }}
              className="font-mono text-sm"
            />
            <Button
              type="button"
              onClick={handleExtrair}
              disabled={loading || !codigo.trim()}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Extrair"}
            </Button>
          </div>
        </div>

        {erro && (
          <div className="flex items-center gap-2 p-3 bg-red-100 text-red-700 rounded-lg text-sm">
            <AlertCircle className="w-4 h-4" />
            {erro}
          </div>
        )}

        {resultado && (
          <div className={`p-3 rounded-lg space-y-2 ${resultado.valido ? "bg-green-100" : "bg-amber-100"}`}>
            <div className={`flex items-center gap-2 font-medium ${resultado.valido ? "text-green-700" : "text-amber-700"}`}>
              <CheckCircle2 className="w-4 h-4" />
              {resultado.valido ? "Dados extraídos com sucesso!" : "Leitura concluída com inconsistências"}
            </div>
            <div className="grid grid-cols-2 gap-2 text-sm">
              {resultado.valor && (
                <div>
                  <span className={resultado.valido ? "text-green-600" : "text-amber-600"}>Valor:</span>
                  <span className="ml-2 font-semibold">{formatCurrency(resultado.valor)}</span>
                </div>
              )}
              {resultado.data_vencimento && (
                <div>
                  <span className={resultado.valido ? "text-green-600" : "text-amber-600"}>Vencimento:</span>
                  <span className="ml-2 font-semibold">
                    {new Date(resultado.data_vencimento + 'T00:00:00').toLocaleDateString('pt-BR')}
                  </span>
                </div>
              )}
              {resultado.fornecedor && (
                <div className="col-span-2">
                  <span className={resultado.valido ? "text-green-600" : "text-amber-600"}>Fornecedor:</span>
                  <span className="ml-2 font-semibold">{resultado.fornecedor}</span>
                </div>
              )}
              {resultado.tipo && (
                <div>
                  <span className={resultado.valido ? "text-green-600" : "text-amber-600"}>Tipo:</span>
                  <span className="ml-2 font-semibold">{resultado.tipo_detalhado || resultado.tipo}</span>
                </div>
              )}
              {resultado.processamento_ms !== undefined && (
                <div>
                  <span className={resultado.valido ? "text-green-600" : "text-amber-600"}>Processamento:</span>
                  <span className="ml-2 font-semibold">{resultado.processamento_ms} ms</span>
                </div>
              )}
            </div>
          </div>
        )}

        <p className="text-xs text-indigo-500">
          Suporta boletos bancários e de concessionárias (água, luz, telefone)
        </p>
      </CardContent>
    </Card>
  );
}
